import { h } from 'preact'
import { useLayoutEffect, useRef, useState } from 'preact/hooks'

//  TODO: import only what's used.
import * as d3 from 'd3'

import styles from '../BarChart.module.scss'
import formatNumber from '../../../utilities/formatNumber'

const translateX = 48
const translateY = 32
const barColor = '#A9DDF7'
const paddingX = 16

export default function LineGraph({ data, xRange, yRange }) {
  const containerRef = useRef(null)
  const chartCanvasRef = useRef(null)

  const [containerWidth, setContainerWidth] = useState(null)

  const [visibleDataSets, setVisibleDataSets] = useState(
    data.reduce((obj, d) => {
      return {
        ...obj,
        [d.key]: {
          active: true,
          label: d.label,
          key: d.key,
          color: d.fill,
        },
      }
    }, {}),
  )

  function toggleDataSet(key) {
    setVisibleDataSets({
      ...visibleDataSets,
      [key]: {
        ...visibleDataSets[key],
        active: !visibleDataSets[key].active,
      },
    })
  }

  function drawChart() {
    const svg = d3.select(chartCanvasRef.current)
    svg.selectAll('*').remove()

    const width = svg.attr('width') - translateX - paddingX
    const height = svg.attr('height') - translateY

    const xScale = d3.scaleLinear().range([0, width])
    const yScale = d3.scaleLinear().range([height, 0])

    xScale.domain(xRange)
    yScale.domain(yRange)

    const g = svg
      .append('g')
      .attr('transform', `translate(${translateX}, ${translateY / 2})`)

    // y axis
    g.append('g')
      .attr('class', styles.grid)
      .call(
        d3
          .axisLeft(yScale)
          .tickFormat(d => d)
          .ticks(10)
          .tickSize(-width - paddingX),
      )
      .append('text')
      .attr('y', 6)
      .attr('dy', '0.71em')
      .attr('text-anchor', 'end')
      .text('value')

    d3.selectAll('#bar-tooltip').remove()

    const tooltip = d3
      .select(containerRef.current)
      .append('div')
      .attr('id', 'bar-tooltip')
      .style('position', 'absolute')
      .style('z-index', '10')
      .style('visibility', 'hidden')
      .style('padding', '10px')
      .style('background', 'rgba(0,0,0,0.6)')
      .style('border-radius', '4px')
      .style('color', '#fff')

    g.selectAll()
      .append('g')
      .attr('class', 'line')
      .data(
        data.map(d => d).filter(d => d.key && visibleDataSets[d.key]?.active),
      )
      .enter()
      .append('path')
      .attr('d', d => {
        return d3
          .line()
          .x(d => xScale(d.i))
          .y(d => yScale(d.val))(d.value)
      })
      .attr('stroke', 'rgb(192,192,192)')
      .attr('fill', d => d.fill)

    g.selectAll()
      .append('g')
      .data(
        data.map(d => d).filter(d => d.key && visibleDataSets[d.key]?.active),
      )
      .enter()
      .append('rect')
      .attr('width', 16)
      .attr('height', 16)
      //   .attr('x', width)
      //   .attr('y', d => {
      //     return yScale(d.max)
      //   })
      .attr('fill', d => d.fill)
      .attr('transform', d => {
        const x = d.rotate ? width - 6 : width - 8
        const y = yScale(d.max) - 8
        const rotation = d.rotate ? 45 : 0

        return `translate(${x}, ${y}) rotate(${rotation})`
      })
      .attr('stroke', '#000')
      .on('mouseover', (e, d) => {
        tooltip
          .html(`<div>$${formatNumber(Number(d.max))}</div>`)
          .style('visibility', 'visible')
          .style('bottom', height - yScale(d.max) + translateY / 2 + 'px')
          .style('left', xScale(1) + translateX + 'px')
      })
  }

  useLayoutEffect(() => {
    if (!containerWidth) {
      const wrapper = containerRef?.current
      const initialWidth = wrapper?.getBoundingClientRect()?.width || 400
      setContainerWidth(initialWidth)
    } else {
      drawChart()
    }
    //  TODO: debounce
    function checkWidth() {
      const wrapper = containerRef?.current
      const newWidth = wrapper?.getBoundingClientRect()?.width || 400
      if (newWidth !== containerWidth) {
        setContainerWidth(newWidth)
      }
    }

    window.addEventListener('resize', checkWidth)

    return () => window.removeEventListener('resize', checkWidth)
  }, [containerWidth, visibleDataSets])

  return (
    <div className={styles.ChartContainer}>
      <div className={styles.GraphContainer} ref={containerRef}>
        <svg
          ref={chartCanvasRef}
          width={containerWidth}
          height={containerWidth * 0.46}
        />
      </div>
      <div className={styles.ChartToggleButtons}>
        {Object.values(visibleDataSets).map(button => {
          return (
            <button
              key={button.key}
              onClick={() => toggleDataSet(button.key)}
              className={`${styles.ChartToggleButton} ${
                button.active ? styles.ChartToggleButtonActive : ''
              }`}
            >
              <div className={styles.ChartToggleButtonInner}>
                <div
                  style={{ backgroundColor: button.color }}
                  className={styles.ChartToggleButtonIndicator}
                />
                <p>{button.label}</p>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
